// In-app debug log (dev aid). A header button opens a panel showing these
// entries so we can diagnose problems without digging through CloudWatch.
//
// To hide the whole feature later, set DEBUG_VISIBLE = false (the panel + header
// button disappear; logging becomes a no-op cost but stays harmless).

export const DEBUG_VISIBLE = true;

export type DebugLevel = "info" | "warn" | "error";

export interface DebugEntry {
  id: number;
  ts: string;
  level: DebugLevel;
  message: string;
  detail?: string;
}

const buffer: DebugEntry[] = [];
const listeners = new Set<() => void>();
let seq = 0;
const MAX = 500;

function emit() {
  for (const fn of listeners) fn();
}

export function debugLog(level: DebugLevel, message: string, detail?: unknown): void {
  const entry: DebugEntry = {
    id: ++seq,
    ts: new Date().toISOString(),
    level,
    message,
    detail:
      detail === undefined
        ? undefined
        : typeof detail === "string"
          ? detail
          : safeStringify(detail),
  };
  buffer.push(entry);
  if (buffer.length > MAX) buffer.shift();
  // Mirror to the real console too.
  const fn = level === "error" ? console.error : level === "warn" ? console.warn : console.log;
  fn(`[${level}] ${message}`, detail ?? "");
  emit();
}

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

export function getLogs(): DebugEntry[] {
  return buffer.slice().reverse(); // newest first
}

// ── Bedrock interaction inspector (admin/dev cost transparency) ─────────────
// A separate, richer store that captures exactly what was sent to and received
// from Bedrock for each AI turn, plus token usage. Surfaced ONLY in the admin
// debug panel — the request payload contains the patient's medical context, so
// it must never be shown to patients or caregivers. Populated from the backend
// `debug` SSE event / `/chat` response, which the API only emits to admins.

export type BedrockMode = "capture" | "onboarding" | "analysis" | "deep" | "second_opinion";

export interface BedrockToolUse {
  name: string;
  input: Record<string, unknown>;
}

export interface BedrockInteraction {
  id: number;
  ts: string; // ISO timestamp the turn completed
  mode: BedrockMode;
  modelId: string;
  /** Configured model that was unavailable; this call ran on `modelId` instead. */
  fellBackFrom?: string;
  /** Exact JSON request body sent to Bedrock (system prompt + messages + tools).
   *  Absent for Deep / Second opinion turns (the digest is large patient data). */
  request?: unknown;
  /** Assistant free-text portion of the response. */
  responseText: string;
  /** Any tool calls the model emitted. */
  toolUses?: BedrockToolUse[];
  inputTokens: number;
  outputTokens: number;
  /** Wall-clock duration of the Bedrock call in ms, when measured client-side. */
  durationMs?: number;
}

const bedrockBuffer: BedrockInteraction[] = [];
const bedrockListeners = new Set<() => void>();
let bedrockSeq = 0;
const BEDROCK_MAX = 100;

function emitBedrock() {
  for (const fn of bedrockListeners) fn();
}

export function recordBedrockInteraction(
  i: Omit<BedrockInteraction, "id" | "ts"> & { ts?: string }
): void {
  const entry: BedrockInteraction = {
    id: ++bedrockSeq,
    ts: i.ts ?? new Date().toISOString(),
    mode: i.mode,
    modelId: i.modelId,
    fellBackFrom: i.fellBackFrom,
    request: i.request,
    responseText: i.responseText,
    toolUses: i.toolUses,
    inputTokens: i.inputTokens,
    outputTokens: i.outputTokens,
    durationMs: i.durationMs,
  };
  bedrockBuffer.push(entry);
  if (bedrockBuffer.length > BEDROCK_MAX) bedrockBuffer.shift();
  // Also drop a one-line breadcrumb into the normal log for at-a-glance scanning.
  debugLog(
    "info",
    `🧠 Bedrock ${entry.mode} (${entry.modelId.split(".").slice(-1)[0] ?? entry.modelId}) — ${entry.inputTokens} in / ${entry.outputTokens} out`
  );
  emitBedrock();
}

export function getBedrockInteractions(): BedrockInteraction[] {
  return bedrockBuffer.slice().reverse(); // newest first
}

export function clearBedrockInteractions(): void {
  bedrockBuffer.length = 0;
  emitBedrock();
}

export function subscribeBedrock(fn: () => void): () => void {
  bedrockListeners.add(fn);
  return () => bedrockListeners.delete(fn);
}

export function errorCount(): number {
  return buffer.filter((e) => e.level === "error").length;
}

export function clearLogs(): void {
  buffer.length = 0;
  emit();
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Capture uncaught errors and promise rejections once, at startup. */
export function installGlobalErrorCapture(): void {
  if (!DEBUG_VISIBLE) return;
  window.addEventListener("error", (e) => {
    debugLog("error", `Uncaught: ${e.message}`, e.error?.stack ?? `${e.filename}:${e.lineno}`);
  });
  window.addEventListener("unhandledrejection", (e) => {
    const r = e.reason;
    debugLog("error", `Unhandled rejection: ${r?.message ?? String(r)}`, r?.stack);
  });
  debugLog("info", "Debug logging started");
}
